import { AssignmentOutlined, SearchOutlined, Visibility } from '@mui/icons-material';
import { CardContainer, DataTable, Tag, SearchBar } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, LoadingState, ErrorState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useApplications } from '@/hooks/queries/useAdminOnboarding';
import type { Application, ReviewStatus } from '@/types/adminOnboarding';

const STATUS_LABELS: Record<ReviewStatus, string> = {
  in_progress: 'En progreso',
  pending_review: 'Pendiente revisión',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const STATUS_VARIANTS: Record<ReviewStatus, 'gray' | 'yellow' | 'green' | 'red'> = {
  in_progress: 'gray',
  pending_review: 'yellow',
  approved: 'green',
  rejected: 'red',
};

const TABS: { label: string; value: ReviewStatus | '' }[] = [
  { label: 'Todas', value: '' },
  { label: 'Pendientes', value: 'pending_review' },
  { label: 'En Progreso', value: 'in_progress' },
  { label: 'Aprobadas', value: 'approved' },
  { label: 'Rechazadas', value: 'rejected' },
];

export const OnboardingReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReviewStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: applications = [],
    isLoading,
    isError,
    refetch,
  } = useApplications(activeTab ? { status: activeTab } : undefined);

  const filtered = useMemo(() => {
    if (!searchTerm) return applications;
    const q = searchTerm.toLowerCase();
    return applications.filter(
      app =>
        app.companyName?.toLowerCase().includes(q) ||
        app.email.toLowerCase().includes(q) ||
        app.userName.toLowerCase().includes(q)
    );
  }, [applications, searchTerm]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const columns: DataTableColumn<Application>[] = [
    {
      key: 'company',
      label: 'Empresa / Admin',
      render: (_, app) => (
        <div>
          <p className="font-medium text-brand-white text-sm">{app.companyName ?? app.userName}</p>
          <p className="text-xs text-muted">{app.email}</p>
        </div>
      ),
    },
    {
      key: 'step',
      label: 'Paso actual',
      render: (_, app) => (
        <span className="text-sm text-brand-white">
          {app.currentStepOrder} — {app.currentStepTitle}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, app) => (
        <Tag variant={STATUS_VARIANTS[app.reviewStatus]}>{STATUS_LABELS[app.reviewStatus]}</Tag>
      ),
    },
    {
      key: 'submittedAt',
      label: 'Enviado',
      render: (_, app) => (
        <span className="text-sm text-muted">
          {app.submittedAt ? formatDate(app.submittedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'days',
      label: 'Días',
      render: (_, app) => (
        <span className="text-sm text-muted">
          {app.daysSinceSubmission != null ? `${app.daysSinceSubmission}d` : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, app) => (
        <button
          onClick={() => void navigate(`/clientes/onboarding-review/${app.userId}`)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-blue border border-brand-blue/30 rounded-lg hover:bg-brand-blue/10 transition-colors"
        >
          <Visibility fontSize="small" />
          Ver detalle
        </button>
      ),
    },
  ];

  if (isError) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <ErrorState
          title="Error al cargar las solicitudes"
          showRetry
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="pt-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[21px] font-bold text-brand-white flex items-center gap-2">
            <AssignmentOutlined />
            Revisión de Onboarding
          </h1>
          <p className="text-muted text-sm mt-1">
            Revisa, aprueba o rechaza las solicitudes de nuevos administradores.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.value
                  ? 'border-brand-blue text-brand-blue'
                  : 'border-transparent text-muted hover:text-brand-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <CardContainer>
          <div>
            <div className="mb-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar por empresa, email o nombre..."
                className="w-full lg:!w-80"
              />
            </div>

            {isLoading ? (
              <LoadingState message="Cargando solicitudes..." />
            ) : filtered.length > 0 ? (
              <DataTable
                columns={columns}
                data={filtered.map(app => ({
                  id: app.userId,
                  ...app,
                }))}
              />
            ) : (
              <EmptyState
                Icon={<SearchOutlined />}
                title="No hay solicitudes"
                description={
                  searchTerm
                    ? 'Intenta con otros términos de búsqueda'
                    : 'No hay solicitudes en este estado'
                }
                useCard={false}
                className="py-10"
              />
            )}
          </div>
        </CardContainer>
      </div>
    </div>
  );
};
